import { useMemo, useRef } from "react";
import type { Alert, ClusterSnapshot } from "../../types";
import { Sparkline } from "../charts/Sparkline";
import { AreaChart } from "../charts/AreaChart";
import { StackedBar } from "../charts/StackedBar";
import { GPUHeatmap } from "../charts/GPUHeatmap";
import { Topology } from "./Topology";
import type { TopologyHandle } from "./Topology";
import { fmtDuration, relTime } from "../../utils/format";
import { CheatsheetButton } from "../CheatsheetButton";
import type { CheatsheetCtx } from "../../cheatsheets/types";
import { useHoverContext } from "../../cheatsheets/provider";
import { alertToCtx } from "../../cheatsheets/alertToCtx";

interface Props {
  snapshot: ClusterSnapshot;
  onDrillNode: (n: string) => void;
  onDrillJob: (id: number) => void;
  onAlertClick: (a: Alert) => void;
}

const last = (arr: number[]): number => (arr.length ? arr[arr.length - 1] : 0);

export function Overview({ snapshot, onDrillNode, onDrillJob, onAlertClick }: Props) {
  const topoRef = useRef<TopologyHandle>(null);
  const gpus = snapshot.gpus;
  const n = snapshot.history_points;
  const totalGpus = gpus.length || 1;
  const busy = gpus.filter((g) => last(g.util) > 30).length;
  const avgUtil = gpus.reduce((a, g) => a + last(g.util), 0) / totalGpus;
  const totalPowerKw = gpus.reduce((a, g) => a + last(g.power_w), 0) / 1000;
  const pending = snapshot.jobs.filter((j) => j.state === "PENDING");
  const running = snapshot.jobs.filter((j) => j.state === "RUNNING");
  const avgWait =
    pending.length
      ? pending.reduce((a, j) => a + (Date.now() - (j.pending_since ?? Date.now())) / 60000, 0) /
        pending.length
      : 0;
  const ibTotal = snapshot.ib_ports.reduce(
    (a, p) => a + last(p.xmit_gbs) + last(p.recv_gbs),
    0
  );
  const ibErrors24h = snapshot.ib_ports.reduce((a, p) => a + p.symbol_errors_total, 0);

  const aggUtil = useMemo(() => {
    const out = new Array(n).fill(0);
    for (let i = 0; i < n; i++) {
      let s = 0;
      gpus.forEach((g) => {
        s += g.util[i] ?? 0;
      });
      out[i] = s / totalGpus;
    }
    return out;
  }, [gpus, n, totalGpus]);

  const aggPower = useMemo(() => {
    const out = new Array(n).fill(0);
    for (let i = 0; i < n; i++) {
      let s = 0;
      gpus.forEach((g) => {
        s += g.power_w[i] ?? 0;
      });
      out[i] = s / 1000;
    }
    return out;
  }, [gpus, n]);

  const ibAgg = useMemo(() => {
    return new Array(n).fill(0).map((_, i) =>
      snapshot.ib_ports.reduce(
        (a, p) => a + (p.xmit_gbs[i] ?? 0) + (p.recv_gbs[i] ?? 0),
        0
      )
    );
  }, [snapshot.ib_ports, n]);

  const nodeOrder = snapshot.nodes.map((n) => n.name);

  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(12, 1fr)", gap: 12 }}>
      <KpiCard
        col="span 3"
        label="GPUs busy"
        value={`${busy}`}
        sub={`/ ${totalGpus} · avg ${avgUtil.toFixed(0)}% util`}
        spark={<Sparkline data={aggUtil} color="var(--cyan)" height={32} max={100} min={0} />}
        accent="var(--cyan)"
        cheatsheet={{ card: "gpus_busy", title: "GPUs busy · cluster-wide", subtitle: `${busy}/${totalGpus} busy · avg ${avgUtil.toFixed(0)}%` }}
      />
      <KpiCard
        col="span 3"
        label="Power draw"
        value={totalPowerKw.toFixed(2)}
        unit="kW"
        sub={`peak ${Math.max(...aggPower, 0).toFixed(2)} kW · PUE 1.12 · 2.4 kW/pkg`}
        spark={<Sparkline data={aggPower} color="var(--violet)" height={32} />}
        accent="var(--violet)"
        cheatsheet={{ card: "power_draw", title: "Power draw", subtitle: `${totalPowerKw.toFixed(2)} kW aggregate` }}
      />
      <KpiCard
        col="span 3"
        label="Queue"
        value={`${running.length}·${pending.length}`}
        sub={`running · pending · avg wait ${avgWait.toFixed(0)}m`}
        spark={<QueueMini running={running.length} pending={pending.length} />}
        accent="var(--amber)"
        cheatsheet={{ card: "queue", title: "Slurm queue", subtitle: `${running.length} running · ${pending.length} pending` }}
      />
      <KpiCard
        col="span 3"
        label="Fabric"
        value={ibTotal.toFixed(0)}
        unit="GB/s"
        sub={`${ibErrors24h} symbol errs · 24h`}
        spark={<Sparkline data={ibAgg} color="var(--blue)" height={32} />}
        accent="var(--blue)"
        cheatsheet={{ card: "fabric", title: "InfiniBand fabric", subtitle: `${ibTotal.toFixed(0)} GB/s · ${ibErrors24h} sym errs 24h` }}
      />

      {/* Topology */}
      <div style={{ gridColumn: "span 8" }}>
        <Topology ref={topoRef} snapshot={snapshot} onDrillNode={onDrillNode} />
      </div>

      {/* Active alerts */}
      <PanelWithCheatsheet
        gridColumn="span 4"
        ctx={{ card: "errors_trend", title: "Active alerts", subtitle: `${snapshot.alerts.length} firing` }}
      >
        <div className="panel-hd">
          <span className="panel-title">Active alerts</span>
          <span className="chip crit">
            <span className="dot pulse" />
            {snapshot.alerts.filter((a) => a.severity === "critical").length} crit
          </span>
          <span className="chip warn">
            <span className="dot" />
            {snapshot.alerts.filter((a) => a.severity === "warning").length} warn
          </span>
          <span className="chip info">
            <span className="dot" />
            {snapshot.alerts.filter((a) => a.severity === "info").length} info
          </span>
        </div>
        <div className="panel-body flush" style={{ overflow: "auto", maxHeight: 360 }}>
          {snapshot.alerts.map((a) => (
            <AlertRow key={a.fingerprint} alert={a} onClick={() => onAlertClick(a)} />
          ))}
          {!snapshot.alerts.length && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--fg-2)" }}>
              No active alerts
            </div>
          )}
        </div>
      </PanelWithCheatsheet>

      {/* Partition utilisation */}
      <PanelWithCheatsheet
        gridColumn="span 5"
        ctx={{ card: "partition", title: "Partition utilization", subtitle: `${snapshot.partitions.length} partitions` }}
      >
        <div className="panel-hd">
          <span className="panel-title">Partition utilization</span>
          <div className="panel-meta">Slurm · sinfo</div>
        </div>
        <div className="panel-body">
          {snapshot.partitions.map((part) => {
            const totalNodes = part.nodes.length;
            const segs = [
              { label: "alloc", value: part.alloc, color: "var(--cyan)" },
              { label: "mix", value: part.mix, color: "var(--blue)" },
              { label: "idle", value: part.idle, color: "var(--emerald)" },
              { label: "drain", value: part.drain, color: "var(--amber)" },
              {
                label: "down",
                value:
                  totalNodes - part.alloc - part.mix - part.idle - part.drain,
                color: "var(--rose)",
              },
            ];
            const busyPct = totalNodes ? (part.alloc / totalNodes) * 100 : 0;
            return (
              <div key={part.name} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    marginBottom: 5,
                  }}
                >
                  <span className="mono" style={{ fontSize: 12, color: "var(--fg-0)" }}>
                    {part.name}
                  </span>
                  <span className="u-xdim mono" style={{ fontSize: 10 }}>
                    {totalNodes} nodes · {part.total_gpus} GPUs · {part.total_cpus} cores
                  </span>
                  <span style={{ flex: 1 }} />
                  <span
                    className="mono"
                    style={{
                      fontSize: 12,
                      color: busyPct >= 100 ? "var(--amber)" : "var(--fg-0)",
                    }}
                  >
                    {busyPct.toFixed(0)}%
                  </span>
                </div>
                <StackedBar segments={segs} />
              </div>
            );
          })}
        </div>
      </PanelWithCheatsheet>

      {/* GPU heatmap */}
      <PanelWithCheatsheet
        gridColumn="span 4"
        ctx={{ card: "gpu_fleet", title: "GPU fleet · live", subtitle: `${gpus.length} GPUs in view` }}
      >
        <div className="panel-hd">
          <span className="panel-title">GPU fleet · live</span>
          <div className="panel-meta">util · temp · power · mem</div>
        </div>
        <div className="panel-body">
          <GPUHeatmap
            gpus={gpus}
            nodeOrder={nodeOrder}
            onSelect={(g) => onDrillNode(g.node)}
          />
        </div>
      </PanelWithCheatsheet>

      {/* Fabric health */}
      <PanelWithCheatsheet
        gridColumn="span 3"
        ctx={{ card: "fabric_health", title: "Scale-out fabric health", subtitle: "Quantum-X800 / CX-9 / XDR" }}
      >
        <div className="panel-hd">
          <span className="panel-title">Scale-out fabric health</span>
          <div className="panel-meta">Quantum-X800 · last 3m</div>
        </div>
        <div className="panel-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FabricTile
              label="CX-9 ports up"
              value={`${snapshot.ib_ports.filter((p) => p.state === 1).length}/${snapshot.ib_ports.length}`}
              ok
            />
            <FabricTile
              label="Sym errs 5m"
              value={String(
                snapshot.ib_ports.reduce(
                  (a, p) =>
                    a +
                    p.symbol_errors
                      .slice(-5)
                      .reduce((x, y) => x + y, 0),
                  0
                )
              )}
            />
            <FabricTile label="SM sweeps" value="2" mono sub="HEALTHY" />
            <FabricTile label="XDR" value="800G" mono sub="MTU 4096B" />
          </div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: 1,
              color: "var(--fg-3)",
              marginTop: 10,
              marginBottom: 4,
            }}
          >
            PORT STATE
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.max(snapshot.ib_ports.length, 1)}, 1fr)`,
              gap: 2,
            }}
          >
            {snapshot.ib_ports.map((p, i) => (
              <div
                key={i}
                title={`${p.node} ${p.hca}/p${p.port} · ${p.speed_gb}Gb`}
                style={{
                  height: 16,
                  borderRadius: 2,
                  background:
                    p.state === 1
                      ? last(p.symbol_errors) > 0
                        ? "var(--amber)"
                        : "var(--emerald)"
                      : "var(--rose)",
                  opacity: p.state === 1 ? 0.75 : 1,
                }}
              />
            ))}
          </div>
        </div>
      </PanelWithCheatsheet>

      {/* Job queue */}
      <PanelWithCheatsheet
        gridColumn="span 7"
        ctx={{ card: "queue", title: "Slurm queue", subtitle: `${running.length} running · ${pending.length} pending` }}
      >
        <div className="panel-hd">
          <span className="panel-title">Job queue</span>
          <span className="chip">{running.length} running</span>
          <span className="chip warn">{pending.length} pending</span>
          <div className="panel-meta">squeue · click row to inspect</div>
        </div>
        <div className="panel-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>JobID</th>
                <th>Name</th>
                <th>User</th>
                <th>Partition</th>
                <th>State</th>
                <th>Nodes</th>
                <th>Elapsed</th>
                <th>Wall</th>
                <th style={{ width: 120 }}>Util</th>
              </tr>
            </thead>
            <tbody>
              {[...running, ...pending].map((j) => {
                const jobGpus = j.gpus
                  .map((gid) => gpus.find((g) => g.id === gid))
                  .filter((g): g is NonNullable<typeof g> => Boolean(g));
                const avgU = jobGpus.length
                  ? jobGpus.reduce((a, g) => a + last(g.util), 0) / jobGpus.length
                  : 0;
                return (
                  <tr key={j.id} className="clickable" onClick={() => onDrillJob(j.id)}>
                    <td className="mono" style={{ color: "var(--cyan)" }}>
                      {j.id}
                    </td>
                    <td>{j.name}</td>
                    <td className="mono u-dim">{j.user}</td>
                    <td className="mono">
                      <span className="chip">{j.partition}</span>
                    </td>
                    <td>
                      <span className={`chip ${j.state === "RUNNING" ? "ok" : "warn"}`}>
                        <span className="dot" />
                        {j.state}
                      </span>
                    </td>
                    <td className="mono u-dim">
                      {j.nodes.join(",") || "—"}
                    </td>
                    <td className="mono">
                      {j.state === "RUNNING"
                        ? fmtDuration(j.elapsed_s)
                        : j.pending_since
                        ? `${fmtDuration((Date.now() - j.pending_since) / 1000)} wait`
                        : "—"}
                    </td>
                    <td className="mono u-dim">{fmtDuration(j.wall_limit_s)}</td>
                    <td>
                      {j.state === "RUNNING" && jobGpus.length ? (
                        <MiniUtilBar value={avgU} />
                      ) : (
                        <span className="u-xdim">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PanelWithCheatsheet>

      {/* Errors trend */}
      <PanelWithCheatsheet
        gridColumn="span 5"
        ctx={{ card: "errors_trend", title: "Errors trend · last 3 min", subtitle: "XID / ECC / IB symbol" }}
      >
        <div className="panel-hd">
          <span className="panel-title">Errors · last 3 min</span>
          <span className="chip crit">
            XID · {gpus.reduce((a, g) => a + g.xid_count, 0)}
          </span>
          <span className="chip warn">
            ECC SBE · {gpus.reduce((a, g) => a + g.ecc_sbe, 0)}
          </span>
          <span className="chip">
            IB sym · {snapshot.ib_ports.reduce((a, p) => a + p.symbol_errors_total, 0)}
          </span>
        </div>
        <div className="panel-body">
          <AreaChart
            series={[
              {
                label: "IB symbol/s",
                data: new Array(n)
                  .fill(0)
                  .map((_, i) =>
                    snapshot.ib_ports.reduce((a, p) => a + (p.symbol_errors[i] ?? 0), 0)
                  ),
                color: "var(--amber)",
              },
            ]}
            height={120}
            unit=""
            yMin={0}
          />
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 8,
              fontSize: 10,
              color: "var(--fg-2)",
              fontFamily: "var(--mono)",
            }}
          >
            <span style={{ color: "var(--amber)" }}>▬ IB symbol errors/s</span>
            <span className="u-xdim">spike thresh: 100/5m</span>
          </div>
        </div>
      </PanelWithCheatsheet>
    </div>
  );
}

function AlertRow({ alert, onClick }: { alert: Alert; onClick: () => void }) {
  const ctx = alertToCtx(alert);
  const hover = useHoverContext(ctx);
  return (
    <div
      {...hover}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        position: "relative",
        width: "100%",
        padding: "10px 34px 10px 12px",
        borderBottom: "1px solid var(--line)",
        textAlign: "left",
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        minHeight: 52,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <div
        style={{
          width: 3,
          alignSelf: "stretch",
          background:
            alert.severity === "critical"
              ? "var(--rose)"
              : alert.severity === "warning"
              ? "var(--amber)"
              : "var(--violet)",
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--fg-0)", fontWeight: 500 }}>
            {alert.name}
          </span>
          <span style={{ flex: 1 }} />
          <span className="u-xdim mono" style={{ fontSize: 10, flexShrink: 0 }}>
            {relTime(alert.fired_at)}
          </span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--fg-1)",
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {alert.summary}
        </div>
      </div>
      <CheatsheetButton ctx={ctx} top={8} right={6} />
    </div>
  );
}

function PanelWithCheatsheet({
  gridColumn,
  ctx,
  children,
}: {
  gridColumn: string;
  ctx: CheatsheetCtx;
  children: React.ReactNode;
}) {
  const hover = useHoverContext(ctx);
  return (
    <div {...hover} className="panel" style={{ gridColumn, position: "relative" }}>
      <CheatsheetButton ctx={ctx} top={8} right={10} />
      {children}
    </div>
  );
}

function KpiCard({
  col,
  label,
  value,
  unit,
  sub,
  spark,
  accent,
  cheatsheet,
}: {
  col: string;
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  spark?: React.ReactNode;
  accent: string;
  cheatsheet?: CheatsheetCtx;
}) {
  const hover = useHoverContext(cheatsheet ?? null);
  return (
    <div
      {...hover}
      className="panel"
      style={{ gridColumn: col, minHeight: 102, position: "relative" }}
    >
      {cheatsheet && <CheatsheetButton ctx={cheatsheet} top={6} right={8} />}
      <div
        className="panel-body"
        style={{ padding: 14, display: "flex", flexDirection: "column", gap: 4 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: accent,
              boxShadow: `0 0 8px ${accent}`,
            }}
          />
          <span className="kpi-label">{label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
          <span className="kpi-val" style={{ color: accent }}>
            {value}
          </span>
          {unit && <span className="kpi-unit">{unit}</span>}
        </div>
        <div className="u-xdim mono" style={{ fontSize: 10 }}>
          {sub}
        </div>
        <div style={{ marginTop: "auto" }}>{spark}</div>
      </div>
    </div>
  );
}

function QueueMini({ running, pending }: { running: number; pending: number }) {
  return (
    <div style={{ display: "flex", height: 28, gap: 2, alignItems: "flex-end" }}>
      {Array.from({ length: running }).map((_, i) => (
        <div
          key={`r${i}`}
          style={{ flex: 1, background: "var(--emerald)", height: "80%", borderRadius: 1 }}
        />
      ))}
      {Array.from({ length: pending }).map((_, i) => (
        <div
          key={`p${i}`}
          style={{
            flex: 1,
            background: "var(--amber)",
            height: "50%",
            borderRadius: 1,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  );
}

function FabricTile({
  label,
  value,
  sub,
  ok,
  mono,
}: {
  label: string;
  value: string;
  sub?: string;
  ok?: boolean;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--line)",
        borderRadius: 4,
        padding: "8px 10px",
      }}
    >
      <div className="kpi-label" style={{ fontSize: 9 }}>
        {label}
      </div>
      <div
        className={mono ? "mono" : ""}
        style={{
          fontSize: 16,
          color: ok ? "var(--emerald)" : "var(--fg-0)",
          fontFamily: "var(--mono)",
          fontWeight: 500,
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {sub && (
        <div className="u-xdim mono" style={{ fontSize: 9 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function MiniUtilBar({ value }: { value: number }) {
  const color =
    value > 75 ? "var(--cyan)" : value > 30 ? "var(--blue)" : "var(--fg-3)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          flex: 1,
          height: 5,
          background: "var(--bg-3)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            background: color,
            transition: "width 400ms",
            boxShadow: `0 0 6px ${color}`,
          }}
        />
      </div>
      <span
        className="mono"
        style={{
          fontSize: 10,
          color,
          minWidth: 28,
          textAlign: "right",
        }}
      >
        {value.toFixed(0)}%
      </span>
    </div>
  );
}
